import { useEffect, useState } from "react";
import {
  LuLibrary,
  LuArrowRight,
  LuChevronLeft,
  LuChevronRight,
  LuMoreHorizontal,
  LuSearch,
} from "react-icons/lu";
import { Link } from "react-router-dom";
import apiClient from "../../api-services/ApiClient";
import Preloader from "../../app-components/preloader/Preloader";
import { SITELINKS } from "../../app-routes/links";
import { formatDate } from "../../helpers";
import { ParamInterface } from "../../interface-and-types";
import { ExcelFile, ExcelSheet, ExcelColumn } from "react-xlsx-wrapper";
import { RiFileExcel2Line } from "react-icons/ri";

const VOActionTracker = () => {
  const [actionTracker, setActionTracker] = useState<any[]>([]);
  const [closeOutActions, setCloseOutActions] = useState<any[]>([]);
  const [actionTrackerExport, setActionTrackerExport] = useState<any[]>([]);
  const [searchInputDebounce, setSearchInputDebounce] = useState<string>("");
  const [searchInput, setSearchInput] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState(10);

  // getActionTracker
  useEffect(() => {
    const params = {
      page: currentPage,
      SearchString: searchInput,
      OrderBy: "Descending",
      FilterByStatus: status,
      PageSize: pageSize,
    };

    getActionTracker(params);
  }, [currentPage, searchInput, status, pageSize]);

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchInput(searchInputDebounce);
      setCurrentPage(1);
    }, 1000);

    return () => {
      clearTimeout(handler);
    };
  }, [searchInputDebounce]);

  useEffect(() => {
    getCloseOutActions();
  }, []);

  // getActionTracker fn
  const getActionTracker = async (params: ParamInterface) => {
    setLoading(true);

    try {
      const res: any = await apiClient.get("ActionTracker", { params });
      if (res && res.data) {
        setActionTracker(res.data.data);
        setCurrentPage(res.data.pageIndex);
        setLastPage(res.data.totalPages);

        const processedData = res.data?.data.map((item: any) => ({
          defectId: item.vesselDefectCheckListId,
          equipmentName: item.vesselDefectCheckList.equipmentName,
          criticalEquipment: item.vesselDefectCheckList.criticalEquipment,
          defectType: item.vesselDefectCheckList.defectType.name,
          comments: item.vesselDefectCheckList.comments,
          priority: item.vesselDefectCheckList.priority,
          priorityDescription: item.vesselDefectCheckList.priorityDescription,
          vesselName: item.vesselDefectCheckList.vessel.name,
          vesselType: item.vesselDefectCheckList.vessel.vesselType,
          imoNumber: item.vesselDefectCheckList.vessel.imoNumber,
          mmsi: item.vesselDefectCheckList.vessel.mmsi,
          responsibleParty: item.responsibleParty,
          resourcesNeeded: item.resourcesNeeded,
          targetCloseOutDate: item.targetCloseOutDate,
          dateClosed: item.dateClosed,
          descriptionOfDefect: item.descriptionOfDefect,
          closeOutAction: item.closeOutAction,
          percentageOfActionClosedOut: item.percentageOfActionClosedOut,
        }));
        setActionTrackerExport(processedData);
      } else {
        setActionTracker([]);
      }
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
      setActionTracker([]);
    } finally {
      setLoading(false);
    }
  };

  // getActionTracker fn
  const getCloseOutActions = async () => {
    setLoading(true);

    try {
      const res: any = await apiClient.get("Constants/CloseOutActions");
      if (res && res.data) {
        setCloseOutActions(res.data.data);
      } else {
        setCloseOutActions([]);
      }
    } catch (error) {
      console.error("Error fetching Inspection Form:", error);
      setCloseOutActions([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle Previous
  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Handle Next
  const handleNext = () => {
    if (currentPage < lastPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  return (
    <>
      <section className="flex justify-between items-center bg-gradient-to-r from-secondaryBlue to-primaryBlue shadow-md border-b border-gray-200">
        <div className="xl:container xl:mx-auto md:px-4 py-4 pb-24 flex justify-between items-center w-full">
          <h4 className="lg:text-[16px] text-white nunito font-medium">
            <span>Action Tracker</span>
          </h4>

          <div className="flex gap-2">
            <ExcelFile
              filename={"action-tracker"}
              element={
                <button className="border-white border-2 flex gap-2 p-1 px-2 rounded-md text-sm items-center text-white font-medium hover:shadow-xl shadow">
                  <RiFileExcel2Line />{" "}
                  <span className="text-xs">Export Table</span>
                </button>
              }
            >
              <ExcelSheet data={actionTrackerExport} name="Inspection History">
                <ExcelColumn label="Defect ID" value="defectId" />
                <ExcelColumn label="Equipment Name" value="equipmentName" />
                <ExcelColumn
                  label="Critical Equipment"
                  value="criticalEquipment"
                />
                <ExcelColumn label="Defect Type" value="defectType" />
                <ExcelColumn label="Comments" value="comments" />
                <ExcelColumn label="Priority" value="priority" />
                <ExcelColumn
                  label="Priority Description"
                  value="priorityDescription"
                />
                <ExcelColumn label="Vessel Name" value="vesselName" />
                <ExcelColumn label="Vessel Type" value="vesselType" />
                <ExcelColumn label="IMO Number" value="imoNumber" />
                <ExcelColumn label="MMSI" value="mmsi" />
                <ExcelColumn
                  label="Responsible Party"
                  value="responsibleParty"
                />
                <ExcelColumn label="Resources Needed" value="resourcesNeeded" />
                <ExcelColumn
                  label="Target Close Out Date"
                  value="targetCloseOutDate"
                />
                <ExcelColumn label="Date Closed" value="dateClosed" />
                <ExcelColumn
                  label="Description of Defect"
                  value="descriptionOfDefect"
                />
                <ExcelColumn label="Close Out Action" value="closeOutAction" />
                <ExcelColumn
                  label="Percentage Closed Out"
                  value="percentageOfActionClosedOut"
                />
              </ExcelSheet>
            </ExcelFile>

            <Link
              to={SITELINKS.VO_CREATE_TRACKER}
              className="rounded-md text-xs bg-red-500 hover:bg-slate-700 shadow hover:shadow-xl flex gap-2 items-center p-1 px-2 text-slate-100 transition-colors duration-300 ease-in-out transform"
            >
              <span>Add New Action Tracker</span>
            </Link>
          </div>
        </div>
      </section>

      {/* page content */}
      <section className="pt-5 mx-auto md:px-4 xl:container relative top-[-100px] xl:top-[-105px]">
        <div className="rounded-lg pb-5 bg-white shadow-sm border">
          {/* page filter && search */}
          <div className="p-4 2xl:p-6 flex justify-between">
            <div className="relative xl:w-1/3 w-1/2">
              <input
                value={searchInputDebounce}
                onChange={(e) => setSearchInputDebounce(e.target.value)}
                placeholder="Search by vessel name, equipment name"
                className="p-2 text-xs border border-gray-300 hover:border-blue-400 rounded-lg w-full"
              />
              <LuSearch className="text-xl top-[7px] absolute right-[9px] text-gray-400" />
            </div>

            <div className="flex gap-2">
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                id="entries"
                className="p-2 py-1.5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
              >
                <option value="10">Show 10 entries</option>
                <option value="25">Show 25 entries</option>
                <option value="50">Show 50 entries</option>
                <option value="100">Show 100 entries</option>
                <option value="250">Show 250 entries</option>
                <option value="500">Show 500 entries</option>
                <option value="1000">Show 1000 entries</option>
              </select>
              <select
                onChange={(e) => {
                  setStatus(e.target.value);
                }}
                className="p-2 py-1.5 text-xs border border-gray-300 hover:border-blue-400 rounded-lg"
              >
                <option value="" disabled>
                  Filter by status
                </option>
                <option value="">All status</option>
                {closeOutActions.map((item: any) => (
                  <option value={item.value}>{item.value}</option>
                ))}
              </select>
            </div>
          </div>

          {/* record TABLE */}
          <div className="overflow-x-auto mb-5 overflow-y-auto">
            <table className="min-w-full bg-white mb-4">
              <thead>
                <tr className="w-full bg-slate-300 text-slate-600 capitalize text-xs leading-normal border-b">
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Vessel Name
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Equipment Name
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Date Reported
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Critical Equipment
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Type of Defect
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Defect Category
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    % Closed Out
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Status
                  </th>
                  <th className="py-1 px-3 text-left font-semibold align-top whitespace-nowrap">
                    Action
                  </th>
                </tr>
              </thead>

              {!!actionTracker?.length && (
                <tbody className="text-[#111827] text-[11px] font-light">
                  {actionTracker?.map((inspection: any, i: number) => {
                    return (
                      <tr
                        key={inspection.id}
                        className={`border-b border-[#F2F1F1] hover:bg-gray-100 font-normal text-[#232630] even:bg-gray-50`}
                      >
                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.vesselDefectCheckList?.vessel?.name ??
                            ""}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.vesselDefectCheckList?.equipmentName ??
                            ""}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.vesselDefectCheckList?.createdAt
                            ? formatDate(
                                inspection?.vesselDefectCheckList?.createdAt,
                                "date"
                              )
                            : ""}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.vesselDefectCheckList
                            ?.criticalEquipment ?? ""}{" "}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.vesselDefectCheckList?.defectType
                            ?.name ?? ""}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.vesselDefectCheckList
                            ?.priorityDescription ?? ""}{" "}
                          ({inspection?.vesselDefectCheckList?.priority ?? ""})
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.percentageOfActionClosedOut ?? ""}
                        </td>

                        <td className="py-2 px-3 text-left align-top">
                          {inspection?.closeOutAction ?? ""}
                        </td>

                        <td className="py-2 px-3 text-left flex gap-4 items-left align-top">
                          <Link
                            to={`${SITELINKS.VO_VIEW_TRACKER}/${inspection.id}`}
                            state={{
                              formType: inspection?.inspectionType?.name ?? "",
                            }}
                            className={`font-medium flex gap-1 items-center text-red-600 hover:text-blue-800`}
                          >
                            <span>View</span>
                            <LuArrowRight />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              )}
            </table>

            {!actionTracker?.length && (
              <div className="text-center p-16 text-sm text-gray-500">
                <LuLibrary className="text-[50px] w-full" />
                <p> No record found</p>
              </div>
            )}
          </div>

          {/* <!-- ROW PAGINATION --> */}
          <div className="flex justify-center gap-3 flex-wrap">
            {/* Previous Button */}
            <button
              onClick={handlePrevious}
              disabled={currentPage === 1}
              className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                currentPage === 1
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <span>Previous</span>
            </button>

            {/* Left Arrow */}
            <button
              disabled={currentPage === 1}
              onClick={handlePrevious}
              className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-medium ${
                currentPage === 1
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <LuChevronLeft className="text-xl" />
            </button>

            {/* Page Numbers */}
            {Array.from(
              { length: lastPage > 7 ? 3 : lastPage },
              (_, i) => i + 1
            ).map((page) => (
              <button
                title={`page ${page}`}
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal text-xs ${
                  page === currentPage
                    ? "bg-red-600 text-white"
                    : "bg-[#fff] text-gray-600"
                }`}
              >
                <span>{page}</span>
              </button>
            ))}

            {lastPage > 7 && (
              <>
                {/* ellipse Arrow */}
                <button
                  disabled
                  className={`bg-[#fff] text-gray-600 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1]`}
                >
                  <LuMoreHorizontal className="text-xl" />
                </button>

                {/* Page Numbers */}
                {Array.from({ length: 3 }, (_, i) => 2 - i).map((page) => (
                  <button
                    title={`page ${lastPage - page}`}
                    key={lastPage - page}
                    onClick={() => setCurrentPage(lastPage - page)}
                    className={`p-1 px-3 rounded-lg border-2 border-solid border-[#F2F1F1] text-base font-normal text-xs ${
                      lastPage - page === currentPage
                        ? "bg-red-600 text-white"
                        : "bg-[#fff] text-gray-600"
                    }`}
                  >
                    <span>{lastPage - page}</span>
                  </button>
                ))}
              </>
            )}

            {/* Right Arrow */}
            <button
              disabled={currentPage === lastPage}
              onClick={handleNext}
              className={`bg-[#fff] text-gray-400 p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] ${
                currentPage === lastPage
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <LuChevronRight className="text-xl" />
            </button>

            {/* Next Button */}
            <button
              onClick={handleNext}
              disabled={currentPage === lastPage}
              className={`bg-[#fff] text-gray-500 text-xs p-1 px-2 rounded-lg border-2 border-solid border-[#F2F1F1] font-normal ${
                currentPage === lastPage
                  ? "disabled:text-gray-400 disabled:cursor-not-allowed"
                  : ""
              }`}
            >
              <span>Next</span>
            </button>
          </div>
        </div>
      </section>

      <Preloader loading={loading} />
    </>
  );
};

export default VOActionTracker;
